'use client';

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Camera, Upload, X, CheckCircle2, FileText, User, BookOpen, Phone, Mail, MapPin, Users, CreditCard, Banknote, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { generateApplicationPDF, type ApplicationFormData } from '@/lib/pdfGenerator';
import { createClient } from '@/lib/supabase/client';

const COURSES = ['ADCA', 'DTP', 'DIFA', 'Tally', 'OM', 'DOE', 'DTTD'];
const BATCHES = [
  'ADCA Jan 2026', 'DTP Mar 2026', 'Tally Aug 2026',
  'DIFA Jan 2026', 'OM Mar 2026', 'DOE May 2026', 'DTTD Mar 2026',
];
const HOUR_PERIODS = [
  'Morning (9AM-12PM)', 'Afternoon (1PM-4PM)', 'Evening (5PM-8PM)',
  'Morning Shift 1 (8AM-10AM)', 'Morning Shift 2 (10AM-12PM)',
  'Afternoon Shift 1 (1PM-3PM)', 'Afternoon Shift 2 (3PM-5PM)',
  'Evening Shift (6PM-8PM)',
];
const QUALIFICATIONS = ['10th Pass', '12th Pass', 'Diploma', 'Graduate', 'Post Graduate', 'Other'];

// Default course fees (fallback if DB fetch fails)
const DEFAULT_COURSE_FEES: Record<string, number> = {
  ADCA: 12000, DTP: 8000, DIFA: 10000, Tally: 6000,
  OM: 7000, DOE: 9000, DTTD: 8500, WEBDEV: 9500,
};

type PhotoMode = 'none' | 'camera' | 'upload';

declare global {
  interface Window {
    Razorpay: any;
  }
}

export default function StudentApplicationPage() {
  const [step, setStep] = useState<'form' | 'preview' | 'done'>('form');
  const [photoMode, setPhotoMode] = useState<PhotoMode>('none');
  const [photoDataUrl, setPhotoDataUrl] = useState<string>('');
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [courseFees, setCourseFees] = useState<Record<string, number>>(DEFAULT_COURSE_FEES);
  const [paymentAmount, setPaymentAmount] = useState<string>('');
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'online'>('cash');
  const [processingPayment, setProcessingPayment] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState({
    fullName: '', dob: '', gender: 'Male', phone: '', email: '',
    address: '', guardianName: '', guardianPhone: '',
    qualification: '12th Pass', course: 'ADCA', batch: '', hourPeriod: 'Morning (9AM-12PM)',
  });

  const supabase = createClient();

  const update = (field: string, value: string) => setForm(prev => ({ ...prev, [field]: value }));

  // Fetch course fees from DB
  useEffect(() => {
    const fetchFees = async () => {
      try {
        const { data } = await supabase
          .from('courses')
          .select('code, name, fee')
          .eq('active', true);
        if (data && data.length > 0) {
          const feeMap: Record<string, number> = { ...DEFAULT_COURSE_FEES };
          data.forEach((c: any) => {
            if (c.code) feeMap[c.code] = c.fee || 0;
            if (c.name) feeMap[c.name] = c.fee || 0;
          });
          setCourseFees(feeMap);
        }
      } catch {
        // Use defaults
      }
    };
    fetchFees();
  }, []);

  // Update payment amount when course changes
  useEffect(() => {
    const fee = courseFees[form.course] || 0;
    setPaymentAmount(fee > 0 ? String(fee) : '');
  }, [form.course, courseFees]);

  const currentCourseFee = courseFees[form.course] || 0;

  // ── Camera ──
  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false });
      setCameraStream(stream);
      setPhotoMode('camera');
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }
      }, 100);
    } catch (err) {
      toast.error('Camera access denied. Please use file upload instead.');
      setPhotoMode('upload');
    }
  }, []);

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
    setPhotoDataUrl(dataUrl);
    stopCamera();
    setPhotoMode('none');
    toast.success('Photo captured!');
  }, []);

  const stopCamera = useCallback(() => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(t => t.stop());
      setCameraStream(null);
    }
  }, [cameraStream]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setPhotoDataUrl(ev.target?.result as string);
      setPhotoMode('none');
      toast.success('Photo uploaded!');
    };
    reader.readAsDataURL(file);
  };

  const generateAppNo = () => `YC-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`;

  // Load Razorpay script dynamically
  const loadRazorpayScript = (): Promise<boolean> => {
    return new Promise((resolve) => {
      if (window.Razorpay) { resolve(true); return; }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleOnlinePayment = async (enrollmentId: string): Promise<boolean> => {
    const amount = parseInt(paymentAmount) || 0;
    if (amount <= 0) {
      toast.error('Please enter a valid payment amount');
      return false;
    }

    setProcessingPayment(true);
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) {
        toast.error('Failed to load payment gateway. Please try again.');
        return false;
      }

      // Create Razorpay order for enrollment
      const res = await fetch('/api/razorpay', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'create_enrollment_order',
          amount,
          enrollmentId,
          studentName: form.fullName,
          studentPhone: form.phone,
          studentEmail: form.email,
          course: form.course,
        }),
      });
      const orderData = await res.json();
      if (!res.ok || !orderData.orderId) {
        toast.error(orderData.error || 'Failed to create payment order');
        return false;
      }

      return new Promise((resolve) => {
        const rzp = new window.Razorpay({
          key: process.env.NEXT_PUBLIC_RAZORPAY_KEY_ID,
          amount: orderData.amount,
          currency: 'INR',
          name: 'YASH Computers',
          description: `Enrollment Fee — ${form.course}`,
          order_id: orderData.orderId,
          prefill: {
            name: form.fullName,
            contact: form.phone,
            email: form.email || '',
          },
          theme: { color: '#1E3A5F' },
          handler: async (response: any) => {
            try {
              // Verify payment and update enrollment record
              const verifyRes = await fetch('/api/razorpay', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  action: 'verify_enrollment_payment',
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                  enrollmentId,
                  amount,
                }),
              });
              const verifyData = await verifyRes.json();
              if (verifyData.verified) {
                toast.success('Payment successful! ₹' + amount.toLocaleString('en-IN'));
                resolve(true);
              } else {
                toast.error('Payment verification failed');
                resolve(false);
              }
            } catch {
              toast.error('Payment verification error');
              resolve(false);
            }
          },
          modal: {
            ondismiss: () => {
              toast.error('Payment cancelled');
              resolve(false);
            },
          },
        });
        rzp.open();
      });
    } catch (err: any) {
      toast.error(err?.message || 'Payment failed');
      return false;
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.fullName || !form.phone || !form.course) {
      toast.error('Please fill in Name, Phone, and Course');
      return;
    }
    const amountToPay = parseInt(paymentAmount) || 0;
    setSubmitting(true);
    try {
      const appNo = generateAppNo();
      const appDate = new Date().toLocaleDateString('en-IN');

      // Save to Supabase enrollment_requests with payment info
      const { data: insertedRow, error: insertError } = await supabase
        .from('enrollment_requests')
        .insert({
          student_name: form.fullName,
          student_phone: form.phone,
          student_email: form.email || null,
          course: form.course,
          batch: form.batch || null,
          hour_period: form.hourPeriod,
          status: 'pending',
          registered_by: 'Self',
          course_fee: currentCourseFee,
          payment_amount: amountToPay,
          payment_method: paymentMethod,
        })
        .select('id')
        .single();

      if (insertError) throw insertError;

      const enrollmentId = insertedRow?.id;

      // If online payment, trigger Razorpay
      if (paymentMethod === 'online' && amountToPay > 0 && enrollmentId) {
        setSubmitting(false);
        const paymentSuccess = await handleOnlinePayment(enrollmentId);
        if (!paymentSuccess) {
          // Payment failed/cancelled — enrollment still saved as pending (cash fallback)
          toast.info('Your application is saved. You can pay at the institute.');
        }
        setSubmitting(true);
      }

      // Generate PDF
      setGeneratingPdf(true);
      const pdfData: ApplicationFormData = {
        ...form,
        photoDataUrl: photoDataUrl || undefined,
        applicationDate: appDate,
        applicationNo: appNo,
      };
      await generateApplicationPDF(pdfData);
      setGeneratingPdf(false);
      setStep('done');
      toast.success('Application submitted! PDF downloaded.');
    } catch (err: any) {
      toast.error(err?.message || 'Submission failed');
    } finally {
      setSubmitting(false);
      setGeneratingPdf(false);
    }
  };

  if (step === 'done') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--background)' }}>
        <div className="max-w-md w-full text-center rounded-2xl p-8" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(16,185,129,0.1)' }}>
            <CheckCircle2 size={32} className="text-success" />
          </div>
          <h2 className="text-xl font-bold text-foreground mb-2">Application Submitted!</h2>
          <div className="rounded-xl p-3 mb-4 text-left" style={{ background: 'rgba(234,179,8,0.08)', border: '1px solid rgba(234,179,8,0.3)' }}>
            <div className="flex items-center gap-2 mb-1">
              <Clock size={14} className="text-yellow-600" />
              <span className="text-sm font-semibold text-yellow-700">Status: Pending Approval</span>
            </div>
            <p className="text-xs text-yellow-700">Your application is under review. You will receive a WhatsApp message once approved by the admin.</p>
          </div>
          <p className="text-sm text-muted-foreground mb-6">
            The PDF has been downloaded to your device. Our team will contact you shortly.
          </p>
          <div className="rounded-xl p-4 mb-6 text-left" style={{ background: 'rgba(30,58,95,0.06)', border: '1px solid rgba(30,58,95,0.15)' }}>
            <p className="text-xs font-semibold text-primary mb-1">KEONICS Franchisee — A Govt of Karnataka Enterprise</p>
            <p className="text-xs text-muted-foreground">YASH Computers | admin@yashcomputers.com</p>
          </div>
          <button onClick={() => { setStep('form'); setForm({ fullName: '', dob: '', gender: 'Male', phone: '', email: '', address: '', guardianName: '', guardianPhone: '', qualification: '12th Pass', course: 'ADCA', batch: '', hourPeriod: 'Morning (9AM-12PM)' }); setPhotoDataUrl(''); setPaymentAmount(''); setPaymentMethod('cash'); }}
            className="w-full py-3 rounded-xl font-semibold btn-primary">
            Submit Another Application
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4" style={{ background: 'var(--background)' }}>
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="rounded-2xl p-6 mb-6 text-center" style={{ background: 'linear-gradient(135deg, #1E3A5F 0%, #2563EB 100%)' }}>
          <div className="flex items-center justify-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <FileText size={20} className="text-white" />
            </div>
            <div className="text-left">
              <h1 className="text-xl font-bold text-white">YASH Computers</h1>
              <p className="text-xs text-blue-200">KEONICS Franchisee — A Govt of Karnataka Enterprise</p>
            </div>
          </div>
          <h2 className="text-lg font-semibold text-white mt-3">Student Application Form</h2>
          <p className="text-sm text-blue-200">Fill in your details to enroll in a course</p>
        </div>

        {/* Pending notice */}
        <div className="rounded-xl p-3 mb-4 flex items-start gap-2" style={{ background: 'rgba(234,179,8,0.08)', border: '1px solid rgba(234,179,8,0.3)' }}>
          <Clock size={15} className="text-yellow-600 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-yellow-700">Your application will be reviewed by the admin. Status will show as <strong>Pending</strong> until approved. You'll receive a WhatsApp notification upon approval.</p>
        </div>

        <div className="rounded-2xl overflow-hidden" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
          <div className="p-6 space-y-6">

            {/* ── Photo Section ── */}
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <User size={15} style={{ color: 'var(--primary)' }} /> Student Photo
              </h3>
              <div className="flex flex-col sm:flex-row gap-4 items-start">
                {/* Photo Preview */}
                <div className="w-28 h-32 rounded-xl flex-shrink-0 overflow-hidden flex items-center justify-center"
                  style={{ border: '2px dashed var(--border)', background: 'var(--input)' }}>
                  {photoDataUrl ? (
                    <div className="relative w-full h-full">
                      <img src={photoDataUrl} alt="Student photo preview" className="w-full h-full object-cover" />
                      <button onClick={() => setPhotoDataUrl('')}
                        className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center">
                        <X size={10} />
                      </button>
                    </div>
                  ) : (
                    <div className="text-center p-2">
                      <User size={24} className="text-muted-foreground mx-auto mb-1" />
                      <p className="text-xs text-muted-foreground">No photo</p>
                    </div>
                  )}
                </div>

                {/* Camera / Upload Options */}
                <div className="flex-1 space-y-3">
                  {photoMode === 'camera' ? (
                    <div className="space-y-2">
                      <video ref={videoRef} autoPlay playsInline muted
                        className="w-full max-w-xs rounded-xl" style={{ border: '1px solid var(--border)' }} />
                      <canvas ref={canvasRef} className="hidden" />
                      <div className="flex gap-2">
                        <button onClick={capturePhoto} className="flex-1 py-2 rounded-xl text-sm font-semibold btn-primary flex items-center justify-center gap-2">
                          <Camera size={15} /> Capture Selfie
                        </button>
                        <button onClick={() => { stopCamera(); setPhotoMode('none'); }}
                          className="px-4 py-2 rounded-xl text-sm font-medium text-muted-foreground" style={{ border: '1px solid var(--border)' }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-xs text-muted-foreground">Choose how to add your photo:</p>
                      <div className="flex flex-wrap gap-2">
                        <button onClick={startCamera}
                          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all"
                          style={{ background: 'rgba(30,58,95,0.08)', border: '1px solid rgba(30,58,95,0.2)', color: 'var(--primary)' }}>
                          <Camera size={15} /> Real-time Selfie
                        </button>
                        <button onClick={() => fileInputRef.current?.click()}
                          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all"
                          style={{ background: 'rgba(37,99,235,0.08)', border: '1px solid rgba(37,99,235,0.2)', color: 'var(--accent)' }}>
                          <Upload size={15} /> Upload Photo
                        </button>
                      </div>
                      <input ref={fileInputRef} type="file" accept="image/*" capture="user"
                        onChange={handleFileUpload} className="hidden" />
                      <p className="text-xs text-muted-foreground">Supported: JPG, PNG (max 5MB). On mobile, upload opens camera.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <hr style={{ borderColor: 'var(--border)' }} />

            {/* ── Personal Information ── */}
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                <User size={15} style={{ color: 'var(--primary)' }} /> Personal Information
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Full Name *</label>
                  <input value={form.fullName} onChange={e => update('fullName', e.target.value)}
                    placeholder="Enter full name as per certificate"
                    className="w-full px-3 py-2.5 rounded-xl text-sm text-foreground outline-none"
                    style={{ background: 'var(--input)', border: '1px solid var(--border)' }} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Date of Birth</label>
                  <input type="date" value={form.dob} onChange={e => update('dob', e.target.value)}
                    className="w-full px-3 py-2.5 rounded-xl text-sm text-foreground outline-none"
                    style={{ background: 'var(--input)', border: '1px solid var(--border)' }} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Gender</label>
                  <select value={form.gender} onChange={e => update('gender', e.target.value)}
                    className="w-full px-3 py-2.5 rounded-xl text-sm text-foreground outline-none"
                    style={{ background: 'var(--input)', border: '1px solid var(--border)' }}>
                    {['Male', 'Female', 'Other'].map(g => <option key={g}>{g}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Phone Number *</label>
                  <div className="relative">
                    <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input value={form.phone} onChange={e => update('phone', e.target.value)}
                      placeholder="10-digit mobile number"
                      className="w-full pl-9 pr-3 py-2.5 rounded-xl text-sm text-foreground outline-none"
                      style={{ background: 'var(--input)', border: '1px solid var(--border)' }} />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Email Address</label>
                  <div className="relative">
                    <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input type="email" value={form.email} onChange={e => update('email', e.target.value)}
                      placeholder="your@email.com"
                      className="w-full pl-9 pr-3 py-2.5 rounded-xl text-sm text-foreground outline-none"
                      style={{ background: 'var(--input)', border: '1px solid var(--border)' }} />
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Address</label>
                  <div className="relative">
                    <MapPin size={14} className="absolute left-3 top-3 text-muted-foreground" />
                    <textarea value={form.address} onChange={e => update('address', e.target.value)}
                      placeholder="Full residential address"
                      rows={2}
                      className="w-full pl-9 pr-3 py-2.5 rounded-xl text-sm text-foreground outline-none resize-none"
                      style={{ background: 'var(--input)', border: '1px solid var(--border)' }} />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Guardian / Parent Name</label>
                  <div className="relative">
                    <Users size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input value={form.guardianName} onChange={e => update('guardianName', e.target.value)}
                      placeholder="Father / Mother / Guardian"
                      className="w-full pl-9 pr-3 py-2.5 rounded-xl text-sm text-foreground outline-none"
                      style={{ background: 'var(--input)', border: '1px solid var(--border)' }} />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Guardian Phone</label>
                  <div className="relative">
                    <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input value={form.guardianPhone} onChange={e => update('guardianPhone', e.target.value)}
                      placeholder="Guardian mobile number"
                      className="w-full pl-9 pr-3 py-2.5 rounded-xl text-sm text-foreground outline-none"
                      style={{ background: 'var(--input)', border: '1px solid var(--border)' }} />
                  </div>
                </div>
              </div>
            </div>

            <hr style={{ borderColor: 'var(--border)' }} />

            {/* ── Course Information ── */}
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                <BookOpen size={15} style={{ color: 'var(--primary)' }} /> Course Information
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Qualification</label>
                  <select value={form.qualification} onChange={e => update('qualification', e.target.value)}
                    className="w-full px-3 py-2.5 rounded-xl text-sm text-foreground outline-none"
                    style={{ background: 'var(--input)', border: '1px solid var(--border)' }}>
                    {QUALIFICATIONS.map(q => <option key={q}>{q}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Course *</label>
                  <select value={form.course} onChange={e => update('course', e.target.value)}
                    className="w-full px-3 py-2.5 rounded-xl text-sm text-foreground outline-none"
                    style={{ background: 'var(--input)', border: '1px solid var(--border)' }}>
                    {COURSES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Batch</label>
                  <select value={form.batch} onChange={e => update('batch', e.target.value)}
                    className="w-full px-3 py-2.5 rounded-xl text-sm text-foreground outline-none"
                    style={{ background: 'var(--input)', border: '1px solid var(--border)' }}>
                    <option value="">Select Batch</option>
                    {BATCHES.map(b => <option key={b}>{b}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Preferred Time Slot</label>
                  <select value={form.hourPeriod} onChange={e => update('hourPeriod', e.target.value)}
                    className="w-full px-3 py-2.5 rounded-xl text-sm text-foreground outline-none"
                    style={{ background: 'var(--input)', border: '1px solid var(--border)' }}>
                    {HOUR_PERIODS.map(h => <option key={h}>{h}</option>)}
                  </select>
                </div>
              </div>
            </div>

            <hr style={{ borderColor: 'var(--border)' }} />

            {/* ── Fee & Payment Section ── */}
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                <CreditCard size={15} style={{ color: 'var(--primary)' }} /> Fee & Payment
              </h3>

              {/* Course Fee Display */}
              <div className="rounded-xl p-4 mb-4" style={{ background: 'rgba(30,58,95,0.06)', border: '1px solid rgba(30,58,95,0.15)' }}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">Course Fee for <strong>{form.course}</strong></p>
                    <p className="text-2xl font-bold text-primary">
                      ₹{currentCourseFee > 0 ? currentCourseFee.toLocaleString('en-IN') : '—'}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'rgba(30,58,95,0.1)' }}>
                    <BookOpen size={20} style={{ color: 'var(--primary)' }} />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">Total course fee. You may pay a partial amount now and the rest later.</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Payment Amount */}
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Amount Paying Now (₹) *</label>
                  <div className="relative">
                    <Banknote size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="number"
                      value={paymentAmount}
                      onChange={e => setPaymentAmount(e.target.value)}
                      placeholder="Enter amount"
                      min="0"
                      max={currentCourseFee || undefined}
                      className="w-full pl-9 pr-3 py-2.5 rounded-xl text-sm text-foreground outline-none"
                      style={{ background: 'var(--input)', border: '1px solid var(--border)' }}
                    />
                  </div>
                  {currentCourseFee > 0 && parseInt(paymentAmount) > 0 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Balance: ₹{Math.max(0, currentCourseFee - (parseInt(paymentAmount) || 0)).toLocaleString('en-IN')}
                    </p>
                  )}
                </div>

                {/* Payment Method */}
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Payment Method</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('cash')}
                      className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-all"
                      style={{
                        background: paymentMethod === 'cash' ? 'rgba(30,58,95,0.12)' : 'var(--input)',
                        border: paymentMethod === 'cash' ? '2px solid var(--primary)' : '1px solid var(--border)',
                        color: paymentMethod === 'cash' ? 'var(--primary)' : 'var(--muted-foreground)',
                      }}
                    >
                      <Banknote size={15} /> Cash
                    </button>
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('online')}
                      className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-all"
                      style={{
                        background: paymentMethod === 'online' ? 'rgba(37,99,235,0.12)' : 'var(--input)',
                        border: paymentMethod === 'online' ? '2px solid #2563EB' : '1px solid var(--border)',
                        color: paymentMethod === 'online' ? '#2563EB' : 'var(--muted-foreground)',
                      }}
                    >
                      <CreditCard size={15} /> Online
                    </button>
                  </div>
                </div>
              </div>

              {paymentMethod === 'online' && (
                <div className="mt-3 rounded-xl p-3 flex items-start gap-2" style={{ background: 'rgba(37,99,235,0.06)', border: '1px solid rgba(37,99,235,0.2)' }}>
                  <CreditCard size={14} className="text-blue-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-blue-700">You will be redirected to Razorpay secure payment gateway after submitting the form. Supports UPI, Cards, Net Banking & Wallets.</p>
                </div>
              )}

              {paymentMethod === 'cash' && (
                <div className="mt-3 rounded-xl p-3 flex items-start gap-2" style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.2)' }}>
                  <Banknote size={14} className="text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-green-700">Pay cash at the institute when you report for admission. Bring this application form.</p>
                </div>
              )}
            </div>

            {/* ── Declaration ── */}
            <div className="rounded-xl p-4 text-xs text-muted-foreground italic"
              style={{ background: 'rgba(30,58,95,0.04)', border: '1px solid rgba(30,58,95,0.1)' }}>
              I hereby declare that the information provided above is true and correct to the best of my knowledge. I agree to abide by the rules and regulations of YASH Computers.
            </div>

            {/* ── Submit ── */}
            <button
              onClick={handleSubmit}
              disabled={submitting || generatingPdf || processingPayment}
              className="w-full py-3 rounded-xl font-semibold btn-primary flex items-center justify-center gap-2 disabled:opacity-60">
              {submitting || generatingPdf || processingPayment ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {processingPayment ? 'Processing Payment...' : generatingPdf ? 'Generating PDF...' : 'Submitting...'}
                </>
              ) : (
                <>
                  {paymentMethod === 'online' ? <CreditCard size={16} /> : <FileText size={16} />}
                  {paymentMethod === 'online' ? 'Submit & Pay Online' : 'Submit Application & Download PDF'}
                </>
              )}
            </button>
          </div>
        </div>

        {/* Branding Footer */}
        <div className="mt-4 text-center">
          <p className="text-xs text-muted-foreground">KEONICS Franchisee — A Govt of Karnataka Enterprise</p>
          <p className="text-xs text-muted-foreground">YASH Computers | admin@yashcomputers.com</p>
        </div>
      </div>
    </div>
  );
}
